// Importation correcte pour résoudre le problème d'autoTable
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
// We can't use React hooks in utility functions, so we'll access localStorage directly

/**
 * Génère un PDF à partir d'une facture
 * @param {Object} invoiceData - Données de la facture
 * @param {Object} memberData - Données du membre associé
 * @returns {jsPDF} Document PDF
 */
export const generateInvoicePDF = (invoiceData, memberData) => {
  // Création du document PDF
  const doc = new jsPDF();

  // Get company settings from localStorage
  let companySettings = {
    companyName: 'STOCKPRO INVENTORY',
    companyNameArabic: 'ستوك برو انفانتري',
    description: 'Système de Gestion d\'Inventaire',
    descriptionSubtitle: 'Solutions logicielles d\'entreprise',
    address: 'Alger Centre, Algérie',
    postalCode: '16000',
    phone: '+213 (0) 21 XX XX XX',
    email: 'contact@stockpro.dz',
    website: 'www.stockpro.dz',
  };

  try {
    const savedSettings = localStorage.getItem('stockpro_company_settings');
    if (savedSettings) {
      companySettings = JSON.parse(savedSettings);
      console.log('PDF: Using company settings from localStorage');
    }
  } catch (error) {
    console.error('Error loading company settings for PDF:', error);
  }

  // Configuration des couleurs et styles
  const primaryColor = '#3730A3'; // Indigo
  doc.setDrawColor(primaryColor);
  doc.setTextColor(40, 40, 40);

  // En-tête de la facture
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('FACTURE', 20, 20);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(companySettings.companyName || 'STOCKPRO INVENTORY', 20, 27);

  // Numéro et date de la facture
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const invoiceNumberWidth = doc.getStringUnitWidth(invoiceData.invoiceNumber) * 16 / doc.internal.scaleFactor;
  doc.text(invoiceData.invoiceNumber, 190 - invoiceNumberWidth, 20);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const issueDateText = `Date: ${new Date(invoiceData.issueDate).toLocaleDateString('fr-FR')}`;
  const issueDateWidth = doc.getStringUnitWidth(issueDateText) * 10 / doc.internal.scaleFactor;
  doc.text(issueDateText, 190 - issueDateWidth, 27);

  if (invoiceData.dueDate) {
    const dueDateText = `Échéance: ${new Date(invoiceData.dueDate).toLocaleDateString('fr-FR')}`;
    const dueDateWidth = doc.getStringUnitWidth(dueDateText) * 10 / doc.internal.scaleFactor;
    doc.text(dueDateText, 190 - dueDateWidth, 34);
  }

  // Ligne de séparation
  doc.setLineWidth(0.5);
  doc.line(20, 36, 190, 36);

  // Informations du client et de l'entreprise
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Facturé à:', 20, 46);

  doc.setFont('helvetica', 'normal');
  if (memberData) {
    doc.setFontSize(10);
    doc.text(`${memberData.firstName} ${memberData.lastName}`, 20, 53);
    doc.text(memberData.email || '', 20, 59);
    doc.text(memberData.phone || '', 20, 65);
    doc.text(`Code membre: ${memberData.memberCode || ''}`, 20, 71);
  }

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('De:', 120, 46);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('CAISSE', 120, 53);
  doc.text('Système de Gestion Financière', 120, 59);
  doc.text('contact@caisse.dz', 120, 65);
  doc.text('+213 XX XX XX XX', 120, 71);

  // Tableau des articles
  const tableStartY = 80;
  const tableColumns = [
    { header: 'Description', dataKey: 'description' },
    { header: 'Quantité', dataKey: 'quantity' },
    { header: 'Prix unitaire', dataKey: 'price' },
    { header: 'Total', dataKey: 'total' }
  ];

  const tableRows = invoiceData.items.map(item => {
    return {
      description: item.description,
      quantity: item.quantity.toString(),
      price: `${item.price.toLocaleString('fr-FR')} DA`,
      total: `${item.total.toLocaleString('fr-FR')} DA`
    };
  });

  autoTable(doc, {
    startY: tableStartY,
    columns: tableColumns,
    body: tableRows,
    theme: 'striped',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { halign: 'center' },
      2: { halign: 'right' },
      3: { halign: 'right' }
    },
    margin: { left: 20, right: 20 }
  });

  // Récupérer la position Y finale du tableau
  const finalY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : tableStartY + 40) + 10;

  // Totaux
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  const rightColumnX = 150;
  const leftColumnX = 120;
  
  doc.text('Sous-total:', leftColumnX, finalY);
  doc.text(`${invoiceData.subtotal.toLocaleString('fr-FR')} DA`, rightColumnX, finalY, { align: 'right' });

  if (invoiceData.discount > 0) {
    doc.setTextColor(220, 53, 69); // Rouge pour les remises
    doc.text('Remise:', leftColumnX, finalY + 7);
    doc.text(`-${invoiceData.discount.toLocaleString('fr-FR')} DA`, rightColumnX, finalY + 7, { align: 'right' });
    doc.setTextColor(40, 40, 40); // Retour à la couleur standard
  }

  if (invoiceData.tax > 0) {
    doc.text('Taxes:', leftColumnX, finalY + 14);
    doc.text(`+${invoiceData.tax.toLocaleString('fr-FR')} DA`, rightColumnX, finalY + 14, { align: 'right' });
  }

  doc.setLineWidth(0.5);
  doc.line(leftColumnX, finalY + 17, 190, finalY + 17);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Total:', leftColumnX, finalY + 25);
  doc.text(`${invoiceData.total.toLocaleString('fr-FR')} DA`, rightColumnX, finalY + 25, { align: 'right' });

  // Notes et conditions
  let noteY = finalY + 40;

  if (invoiceData.notes) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', 20, noteY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(invoiceData.notes, 20, noteY + 7);
    noteY += 15;
  }

  if (invoiceData.terms) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Conditions de paiement:', 20, noteY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(invoiceData.terms, 20, noteY + 7);
  }

  // Pied de page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('Merci pour votre confiance !', doc.internal.pageSize.width / 2, 280, { align: 'center' });
    doc.text(`Page ${i} sur ${pageCount}`, 190, 287, { align: 'right' });
  }

  return doc;
};

/**
 * Exporte une facture au format PDF à partir des données
 * @param {Object} invoiceData - Données de la facture
 * @param {Object} memberData - Données du membre associé
 */
export const downloadInvoicePDF = (invoiceData, memberData) => {
  const doc = generateInvoicePDF(invoiceData, memberData);
  doc.save(`Facture_${invoiceData.invoiceNumber}.pdf`);
};

/**
 * Génère un PDF à partir d'un élément HTML
 * @param {HTMLElement} element - Élément HTML à convertir
 * @param {string} filename - Nom du fichier PDF
 * @param {Function} callback - Fonction de rappel après génération
 */
export const generatePDFFromHTML = async (element, filename, callback = null) => {
  const canvas = await html2canvas(element, {
    scale: 2,
    logging: false,
    useCORS: true
  });

  const imgData = canvas.toDataURL('image/jpeg', 0.95);
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const imgWidth = 210; // A4 width in mm
  const pageHeight = 297; // A4 height in mm
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let heightLeft = imgHeight;
  let position = 0;
  let pageData = imgData;

  pdf.addImage(pageData, 'JPEG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  while (heightLeft >= 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(pageData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  if (callback && typeof callback === 'function') {
    callback(pdf);
  } else {
    pdf.save(filename);
  }
}; 